import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, firstValueFrom, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { QaiToastService } from '@qai/ui';
import { TranslocoService } from '@jsverse/transloco';
import { TranslationService } from '../translation.service';
import { ResultsGuidedService } from './results-guided-service';
import { PathwayLocalStorageUtil, KcPollingContext } from 'src/app/utils/pathway-localstorage.util';
import { AppConstants } from 'src/app/app-constants';
import { IResultsGuided } from 'src/app/interfaces/iresults-guided';
import { IPathwayQuizResult } from 'src/app/interfaces/ipathway-quiz-result';
import { LoggerService } from '../logger/logger.service';

const SUPPRESSED_ROUTES = [
  '/call',
  '/assessment-pre-call',
  '/assessment-call',
  '/results_guided',
  '/results_conversational',
  '/result_personalized_practice',
];

const POLLING_INTERVAL_MS = 20_000;
const POLLING_DURATION_MS = 300_000;

@Injectable({ providedIn: 'root' })
export class KcResultsPollingService {
  private isPolling = false;
  private pollingTimeout: ReturnType<typeof setTimeout> | null = null;
  private pollingStartTime = 0;
  private pendingToastCtx: KcPollingContext | null = null;
  private routerSub: Subscription | null = null;

  constructor(
    private readonly router: Router,
    private readonly resultsGuidedService: ResultsGuidedService,
    private readonly toastService: QaiToastService,
    private readonly translocoService: TranslocoService,
    private readonly translationService: TranslationService,
    private readonly logger: LoggerService,
  ) {}

  async init(): Promise<void> {
    if (this.routerSub) return;
    await this.translationService.loadScopedTranslation('results');
    this.logger.debug('[KcResultsPolling] init - subscribing to router events');
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.onNavigationEnd(e));
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const url = event.urlAfterRedirects;
    const ctx = PathwayLocalStorageUtil.getKcPollingContext();
    this.logger.debug('[KcResultsPolling] navigation end', { url, ctx, isPolling: this.isPolling });
    if (ctx && !this.isPolling) {
      this.logger.debug('[KcResultsPolling] starting polling', { ctx });
      this.startPolling(ctx);
    }
    if (this.pendingToastCtx && !this.isSuppressedRoute(url)) {
      this.logger.debug('[KcResultsPolling] showing pending toast', { ctx: this.pendingToastCtx });
      this.showToast(this.pendingToastCtx);
      this.pendingToastCtx = null;
    }
  }

  private isSuppressedRoute(url: string): boolean {
    return SUPPRESSED_ROUTES.some(r => url.startsWith(r));
  }

  private startPolling(ctx: KcPollingContext): void {
    this.isPolling = true;
    this.pollingStartTime = Date.now();

    const poll = async (): Promise<void> => {
      if (!this.isPolling) return;
      this.logger.debug('[KcResultsPolling] poll tick', { practiceCallUuid: ctx.practiceCallUuid, isPathwayFlow: ctx.isPathwayFlow });
      try {
        const result$ = (ctx.isPathwayFlow
          ? this.resultsGuidedService.getPathwayQuizResultData(ctx.practiceCallUuid)
          : this.resultsGuidedService.getResultsGuidedData(ctx.practiceCallUuid)
        ) as Observable<IResultsGuided | IPathwayQuizResult>;
        const result = await firstValueFrom(result$);
        if (result?.practiceCall?.scoredAt != null) {
          this.logger.debug('[KcResultsPolling] result ready, stopping polling', { practiceCallUuid: ctx.practiceCallUuid });
          this.stopPolling();
          PathwayLocalStorageUtil.clearKcPollingContext();
          if (this.isSuppressedRoute(this.router.url)) {
            this.logger.debug('[KcResultsPolling] current route suppressed, deferring toast');
            this.pendingToastCtx = ctx;
          } else {
            this.showToast(ctx);
          }
          return;
        }
      } catch (error) {
        this.logger.debug('[KcResultsPolling] poll request failed, will retry', { practiceCallUuid: ctx.practiceCallUuid, error });
      }

      if (!this.isPolling) return;

      if (Date.now() - this.pollingStartTime >= POLLING_DURATION_MS) {
        this.logger.debug('[KcResultsPolling] polling duration exceeded, stopping', { practiceCallUuid: ctx.practiceCallUuid });
        this.stopPolling();
        PathwayLocalStorageUtil.clearKcPollingContext();
        return;
      }

      this.pollingTimeout = setTimeout(() => poll(), POLLING_INTERVAL_MS);
    };

    poll();
  }

  private stopPolling(): void {
    this.isPolling = false;
    if (this.pollingTimeout) {
      clearTimeout(this.pollingTimeout);
      this.pollingTimeout = null;
    }
  }

  private showToast(ctx: KcPollingContext): void {
    const toastId = `toast-kc-${Date.now()}`;
    const scope = 'results';
    this.toastService.addMessage({
      id: toastId,
      kind: 'neutral',
      iconShape: 'square',
      title: ctx.isPathwayFlow
        ? this.translocoService.translate('KC_TOAST_TITLE_PATHWAY', { stepName: ctx.stepName ?? '' }, scope)
        : this.translocoService.translate('KC_TOAST_TITLE', {}, scope),
      content: ctx.isPathwayFlow
        ? this.translocoService.translate('KC_TOAST_CONTENT', {}, scope)
        : ' ',
      buttons: [
        {
          label: this.translocoService.translate('DISMISS', {}, scope),
          kind: 'tertiary-text',
          onClick: () => this.toastService.removeMessage(toastId),
        },
        {
          label: this.translocoService.translate('VIEW_RESULTS', {}, scope),
          onClick: () => {
            this.toastService.removeMessage(toastId);
            this.router.navigate(['/results_guided'], {
              queryParams: {
                practiceCallUuid: ctx.practiceCallUuid,
                ...(ctx.isPathwayFlow ? { practiceType: AppConstants.TYPE_PATHWAY } : {}),
              },
            });
          },
        },
      ],
    });
  }
}
